import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

const SimpleHero: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isKorean = i18n.language === 'ko';

  return (
    <section className="min-h-screen relative flex items-center justify-center bg-gradient-to-br from-green-50 via-white to-emerald-50 overflow-hidden pt-16">
      {/* Background - subtle */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-green-200 rounded-full blur-3xl opacity-30" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-emerald-200 rounded-full blur-3xl opacity-20" />
      </div>

      {/* Main content */}
      <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="space-y-6 sm:space-y-8">

          {/* GomGomi Circle */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="flex justify-center pt-4 sm:pt-8"
          >
            <div className="relative group">
              <div className="w-28 h-28 sm:w-36 sm:h-36 md:w-44 md:h-44 lg:w-48 lg:h-48 rounded-full bg-white shadow-xl overflow-hidden border-4 border-green-100">
                <img
                  src="/images/gomgomi.webp"
                  alt="GomGomi"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-bold text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-gray-900 leading-tight px-2"
          >
            <span className="block whitespace-nowrap">
              {t('hero.title1')}{" "}
              <span className="text-green-600">{t('hero.title2')}</span>
            </span>
            <span className="block mt-1 sm:mt-2 whitespace-nowrap">
              {t('hero.title3')}{" "}
              <span className="text-green-600">{t('hero.title4')}</span>
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            {t('hero.subtitle')}
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 justify-center pt-4"
          >
            <Link to="/projects">
              <button className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-xl font-semibold transition-colors text-base">
                {t('hero.cta.start')}
              </button>
            </Link>
            <Link to="/memes">
              <button className="w-full sm:w-auto bg-white border-2 border-green-500 text-green-600 hover:bg-green-50 px-8 py-4 rounded-xl font-semibold transition-colors text-base">
                {t('hero.cta.view')}
              </button>
            </Link>
          </motion.div>

          {/* Story Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="pt-12 sm:pt-16 pb-12 sm:pb-16 max-w-2xl mx-auto px-2"
          >
            <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-6 sm:p-8 md:p-10 shadow-2xl border border-green-100">
              {isKorean ? (
                <div className="text-left text-gray-600 space-y-5 text-sm sm:text-base md:text-lg leading-relaxed">
                  <p>
                    대 AI 시대, 개발자에게는 대혼돈의 시대가 열렸습니다.
                  </p>
                  <p>
                    취업 시장은 얼어붙고, 컴퓨터공학 학위의 가치는 끝없이 흔들리고 있죠.<br />
                    기술은 하루가 다르게 변하고, 어제의 정답이 오늘의 오답이 되기도 합니다.
                  </p>
                  <p>
                    그래도 변하지 않는 게 있다고 생각해요.
                  </p>
                  <p>
                    개발자의 본질은 결국 <span className="text-green-600 font-semibold">문제 해결</span>이니까요.
                  </p>
                  <p>
                    도구가 바뀌어도, 언어가 바뀌어도,<br />
                    세상의 불편함을 발견하고 해결하는 건 여전히 재밌는 일이에요.<br />
                    곰곰이 코딩랩은 바로 그 본질을 파고들어 보려 합니다.
                  </p>
                  <p className="text-gray-500">
                    거대한 과도기 속에서 곰곰이는 살아남을 수 있을까요?<br />
                    과연 곰곰이의 운명은...?
                  </p>
                </div>
              ) : (
                <div className="text-left text-gray-600 space-y-5 text-sm sm:text-base md:text-lg leading-relaxed">
                  <p>
                    In the age of AI, it's a wild time to be a developer.
                  </p>
                  <p>
                    The job market is tough, and that CS degree doesn't feel as solid as it used to.<br />
                    Tech moves fast—what worked yesterday might not work today.
                  </p>
                  <p>
                    But some things don't change.
                  </p>
                  <p>
                    At the end of the day, developers <span className="text-green-600 font-semibold">solve problems</span>.
                  </p>
                  <p>
                    Tools change. Languages change.<br />
                    But spotting what's broken and fixing it? That's still the fun part.<br />
                    That's what Bearly Coding Lab is all about.
                  </p>
                  <p className="text-gray-500">
                    Will Bearly make it through this crazy transition?<br />
                    Stay tuned...
                  </p>
                </div>
              )}
              {/* GomGomi2 Image */}
              <div className="flex justify-center mt-8">
                <img
                  src="/images/gomgomi2.webp"
                  alt="GomGomi coding"
                  className="w-44 sm:w-52 md:w-60 h-auto"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SimpleHero;
